import axios from 'axios'
import { pluckData } from 'src/utilities/http-client'
import type {
  AccreditationV2,
  CategoryResponse,
  EventAccreditation,
  Instructor,
} from 'src/v3/core/types'
import type {
  ManageEvent,
  ManageEventTemplate,
  ManageEventTemplateJoin,
} from '../manage-events.slice'
import type {
  EventAttendeesResponse,
  EventAttendeeRow,
  EventCapacityResponse,
  EventInviteItem,
  EventInviteStatus,
  EventSessionsResponse,
  SessionCapacityLimits,
} from '../types'
import {
  MANAGE_EVENT_API,
  MANAGE_EVENTS_API,
  MANAGE_EVENT_ACCREDITATIONS_API,
  MANAGE_EVENT_ACCREDITATION_JOINS_API,
  MANAGE_EVENT_CATEGORIES_API,
  MANAGE_EVENT_INSTRUCTORS_API,
  MANAGE_EVENT_INVITE_BASE_API,
  MANAGE_EVENT_SESSION_BASE_API,
  MANAGE_EVENT_TEMPLATE_JOINS_API,
  MANAGE_EVENT_TEMPLATES_API,
} from './constants'

export let fetchEvents = () =>
  axios.get<ManageEvent[]>(MANAGE_EVENTS_API).then(pluckData)

export let fetchEventInstructors = () =>
  axios.get<Instructor[]>(MANAGE_EVENT_INSTRUCTORS_API).then(pluckData)

export let fetchEventCategories = () =>
  axios.get<CategoryResponse>(MANAGE_EVENT_CATEGORIES_API).then(pluckData)

export let fetchEventAccreditations = () =>
  axios
    .get<{ [kind: string]: AccreditationV2 }[]>(MANAGE_EVENT_ACCREDITATIONS_API)
    .then(pluckData)

export let fetchEventAccreditationJoins = () =>
  axios
    .get<EventAccreditation[]>(MANAGE_EVENT_ACCREDITATION_JOINS_API)
    .then(pluckData)

export let fetchEventTemplates = () =>
  axios.get<ManageEventTemplate[]>(MANAGE_EVENT_TEMPLATES_API).then(pluckData)

export let fetchEventTemplateJoins = () =>
  axios
    .get<ManageEventTemplateJoin[]>(MANAGE_EVENT_TEMPLATE_JOINS_API)
    .then(pluckData)

export let searchSessions = (eventID: string, q: string) =>
  axios
    .get<EventSessionsResponse>(`${MANAGE_EVENT_API}/${eventID}/sessions`, {
      params: { q, pageSize: 50 },
    })
    .then(pluckData)
    .then((r) => ({
      items: r.items.map((s) => ({
        id: s.id,
        title: s.name ?? '',
        ts: s.sessionTime,
        timeZone: s.timeZone,
      })),
    }))

type SessionFilters = {
  q?: string
  instructor?: string
  status?: string
  dateFrom?: string
  dateTo?: string
}

export let fetchEventSessions = (
  eventID: string,
  page = 1,
  pageSize = 10,
  filters: SessionFilters = {},
) =>
  axios
    .get<EventSessionsResponse>(`${MANAGE_EVENT_API}/${eventID}/sessions`, {
      params: { page, pageSize, ...filters },
    })
    .then(pluckData)

export let fetchSessionCapacityLimits = (sessionID: string) =>
  axios
    .get<SessionCapacityLimits | null>(
      `${MANAGE_EVENT_SESSION_BASE_API}/${sessionID}/capacity`,
    )
    .then(pluckData)

type AttendeeFilters = {
  scope?: string
  range?: string
  sessionId?: string
  q?: string
  status?: string
  license?: string
}

type RawInviteeItem = {
  userID: string
  inviteID: string | null
  firstName: string
  lastName: string
  primaryEmail: string
  licenseType: string
  status: string | null
}

let VALID_STATUSES: ReadonlySet<string> = new Set([
  'invited',
  'accepted',
  'declined',
  'waitlisted',
  'attended',
])

export let fetchEventAttendees = (
  eventID: string,
  page = 1,
  pageSize = 10,
  filters: AttendeeFilters = {},
): Promise<EventAttendeesResponse> =>
  axios
    .get<{ items: RawInviteeItem[]; total: number }>(
      `${MANAGE_EVENT_API}/${eventID}/invitees`,
      { params: { page, pageSize, ...filters } },
    )
    .then(pluckData)
    .then((r) => ({
      total: r.total,
      items: r.items.map((item): EventAttendeeRow => ({
        id: item.inviteID ?? item.userID,
        userID: item.userID,
        inviteID: item.inviteID,
        firstName: item.firstName,
        lastName: item.lastName,
        primaryEmail: item.primaryEmail,
        licenseType: item.licenseType,
        status: VALID_STATUSES.has(item.status ?? '')
          ? (item.status as EventAttendeeRow['status'])
          : null,
      })),
    }))

export let fetchEventCapacity = (
  eventID: string,
  params: { scope?: string; range?: string; sessionId?: string },
) =>
  axios
    .get<EventCapacityResponse>(`${MANAGE_EVENT_API}/${eventID}/capacity`, {
      params,
    })
    .then(pluckData)

export let updateEventInviteStatus = (
  inviteID: string,
  status: EventInviteStatus,
) =>
  axios
    .put<{ status: EventInviteStatus; capacityWarning: string | null }>(
      `${MANAGE_EVENT_INVITE_BASE_API}/${inviteID}/status`,
      { status },
    )
    .then(pluckData)

export let fetchSessionInvites = (sessionID: string) =>
  axios
    .get<EventInviteItem[]>(
      `${MANAGE_EVENT_SESSION_BASE_API}/${sessionID}/invites`,
    )
    .then(pluckData)

// Both bulk routes reject an empty list (400), so callers skip the call instead.
export let addSessionInvites = (sessionID: string, userIDs: string[]) =>
  axios
    .post<EventInviteItem[]>(
      `${MANAGE_EVENT_SESSION_BASE_API}/${sessionID}/invites`,
      { userIDs },
    )
    .then(pluckData)

export let removeSessionInvites = (sessionID: string, userIDs: string[]) =>
  axios
    .post<EventInviteItem[]>(
      `${MANAGE_EVENT_SESSION_BASE_API}/${sessionID}/invites/remove`,
      { userIDs },
    )
    .then(pluckData)
